/**
 * @file Consumer custom print checkout.
 */

import React, { Fragment, useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import StripeCheckout from 'react-stripe-checkout';
import get from 'lodash.get';

import { getAlbumCustomPrintName } from '../../../../../util/custom-prints';
import { formatPriceForDisplay } from '../../../../../util/format-money';
import CustomPrintConfigurator from '../../../../../components/global/CustomPrintConfigurator';
import { getAsset } from '../../../../../util/assets';
import Button from '../../../../../components/global/Button';
import getConfig from '../../../../../util/config';
import useCustomPrintCheckoutState from '../../../hooks/useCustomPrintCheckoutState';
import IdentitySelectListModal from '../../../../../components/global/identity/IdentitySelectListModal';
import IdentitySelectToggle from '../../../../../components/global/identity/IdentitySelectToggle';
import { CURRENCY } from '../../../../../constants/currency';
import CustomPrintCheckoutSuccessModal from './CustomPrintCheckoutSuccessModal';
import { postNativeAppCustomConfiguratorComplete } from '../../../../../services/native-bridge/native-apps-interface';

const propTypes = {
  loading: PropTypes.bool,
  account: PropTypes.shape({
    id: PropTypes.string.isRequired,
    linked_identities: PropTypes.shape({
      records: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
          name: PropTypes.string,
        }),
      ).isRequired,
    }).isRequired,
  }),
  checkout: PropTypes.func.isRequired,
  albumCustomPrint: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  candidatePhotoGroups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      photos: PropTypes.array,
      label: PropTypes.string,
      total: PropTypes.number,
      loadMore: PropTypes.func,
    }),
  ),
  accountAddressCreate: PropTypes.func.isRequired,
  openModal: PropTypes.func,
  price: PropTypes.shape({
    total_price: PropTypes.shape({
      value: PropTypes.number,
      currency: PropTypes.oneOf(Object.values(CURRENCY)),
    }),
  }),
  onComplete: PropTypes.func,
  onCheckoutSuccess: PropTypes.func,
};

const CustomPrintCheckout = ({
  albumCustomPrint,
  candidatePhotoGroups,
  checkout,
  account,
  loading,
  openModal,
  accountAddressCreate,
  price,
  onComplete,
  onCheckoutSuccess,
}) => {
  const {
    state: { activeIdentityId },
    setActiveIdentityId,
  } = useCustomPrintCheckoutState();
  const stripeToken = useRef(null);
  const [completed, setCompleted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [identityModalOpen, setIdentityModalOpen] = useState(false);

  const accountIdentities = get(account, 'linked_identities.records', []);
  const activeIdentity = accountIdentities.find(
    ({ id }) => id === activeIdentityId,
  );

  // If the user has only one identity skip selection
  // and automatically set the first identity (the only one) as active.
  useEffect(() => {
    if (accountIdentities.length === 1) {
      const [firstIdentity] = accountIdentities;
      setActiveIdentityId(firstIdentity.id);
    }
  }, [account && account.id]);

  const onCheckout = async ({
    albumCustomPrint: print,
    caption,
    photos,
    address,
  }) => {
    if (!stripeToken || !stripeToken.current) {
      return;
    }

    const data = {
      accountId: account.id,
      accountAddressId: address && address.id,
      stripeTokenId: stripeToken.current.id,
      email: stripeToken.current.email,
      stripePaymentMethodId: stripeToken.current.stripe_payment_method_id,
      stripeConnectAccountId: stripeToken.current.stripe_connect_account_id,
      customPrintItems: [
        {
          album_custom_print_id: print.id,
          identity_id: activeIdentityId,
          quantity: 1,
          configuration: {
            custom_print_caption: caption,
            photos,
          },
        },
      ],
    };

    if (address && address.id) {
      data.account_address_id = address.id;
    }

    if (address && !address.id) {
      data.address = address;
    }

    await checkout(data);

    if (onCheckoutSuccess) {
      onCheckoutSuccess(albumCustomPrint);
    }
  };

  const onStripeTokenReceived = async (token, onSubmit) => {
    if (!stripeToken || !account) {
      return;
    }

    stripeToken.current = token;
    await onSubmit();
  };

  const customPrintName = getAlbumCustomPrintName(albumCustomPrint);
  const finalPrice = get(price, 'total_price.value', 0);
  const config = getConfig();

  return (
    <Fragment>
      {activeIdentity && accountIdentities.length > 1 && (
        <IdentitySelectToggle
          identity={activeIdentity}
          onClick={() => setIdentityModalOpen(true)}
          className="mb-3"
        />
      )}

      <CustomPrintConfigurator
        key={activeIdentityId}
        loading={loading}
        albumCustomPrint={albumCustomPrint}
        account={account}
        accountAddressCreate={accountAddressCreate}
        openModal={openModal}
        candidatePhotoGroups={candidatePhotoGroups}
        onSubmit={onCheckout}
        onSubmitSuccess={() => setSuccess(true)}
        completed={completed}
        renderSubmitButton={({ submitting, disabled, onSubmit }) => (
          <StripeCheckout
            name="Waldo Photos"
            description={`${customPrintName} Checkout`}
            image={getAsset('/waldo-stripe-logo.png')}
            token={token => onStripeTokenReceived(token, onSubmit)}
            panelLabel={`Pay $${formatPriceForDisplay(finalPrice)}`}
            stripeKey={config.stripe.key}
            currency="USD"
            ComponentClass="div"
            zipCode
            disabled={disabled || !finalPrice}
          >
            <Button
              className="btn-custom-print-config-submit mt-2"
              btnSize="lg"
              btnBlock
              disabled={disabled || !finalPrice}
            >
              {submitting || !finalPrice
                ? 'Please wait…'
                : `Buy your ${customPrintName} - $${formatPriceForDisplay(
                    finalPrice,
                  )}`}
            </Button>
          </StripeCheckout>
        )}
      />

      {account && (
        <IdentitySelectListModal
          identities={accountIdentities}
          onIdentitySelect={id => {
            setActiveIdentityId(id);
            setIdentityModalOpen(false);
          }}
          forceOpen={!activeIdentity || identityModalOpen}
          renderHeader={() => (
            <div className="mb-3">
              <p className="mb-0">
                <strong className="d-block text-dark">Select a face</strong>
                <span className="text-sm text-italic">
                  Select who this {customPrintName} is for.
                </span>
              </p>
            </div>
          )}
        />
      )}

      {success && (
        <CustomPrintCheckoutSuccessModal
          name={customPrintName}
          onReset={() => {
            if (accountIdentities.length > 1) {
              setIdentityModalOpen(true);
            }

            setSuccess(false);

            window.scrollTo({
              top: 0,
              behavior: 'smooth',
            });
          }}
          onComplete={() => {
            if (onComplete) {
              onComplete(albumCustomPrint);
              return;
            }

            setCompleted(true);
            setSuccess(false);
            postNativeAppCustomConfiguratorComplete();
          }}
          forceOpen={success}
        />
      )}
    </Fragment>
  );
};

CustomPrintCheckout.propTypes = propTypes;

export default CustomPrintCheckout;
